import React from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Typography,
  FormHelperText,
} from "@mui/material";
import RequiredLabel from "./RequiredLabel";

/**
 * 複数選択フィールドコンポーネント
 *
 * @param {string}   label          表示ラベル
 * @param {string}   inputLabel     Select の枠ラベル
 * @param {string}   name           フィールド名
 * @param {Array<number|string>} value 選択中のID配列
 * @param {function} onChange       値変更ハンドラ (ids: Array) => void
 * @param {Array<{id:number|string,name:string}>} options 選択肢
 * @param {boolean}  [isRequired=false] 必須表示フラグ
 * @param {object}   [error=false]   React Hook Form の error オブジェクト
 * @param {string}   [helperText=""]  補足テキスト（任意）
 */
export default function MultiSelectField({
  label,
  inputLabel,
  name,
  value = [],
  onChange,
  options = [],
  isRequired = false,
  error = false,
  helperText = "",
}) {
  const handleChange = (e) => onChange(e.target.value);

  const renderSelected = (selected) => {
    if (!selected || selected.length === 0) return "";
    const names = selected
      .map((id) => options.find((o) => o.id === id)?.name || "")
      .filter(Boolean);
    return names.join(", ");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography>{label}</Typography>
        <RequiredLabel show={isRequired} />
      </Box>

      <FormControl fullWidth error={Boolean(error)}>
        {inputLabel && <InputLabel>{inputLabel}</InputLabel>}
        <Select
          multiple
          name={name}
          value={value}
          onChange={handleChange}
          label={inputLabel}
          renderValue={renderSelected}
          native={false}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 400,
                width: { xs: "95vw", sm: 300 },
              },
            },
          }}
        >
          {options.map((opt) => (
            <MenuItem
              key={opt.id}
              value={opt.id}
              sx={{
                display: "flex",
                alignItems: "center",
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            >
              <Checkbox
                checked={value.includes(opt.id)}
                sx={{ mr: 1, flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                }}
              >
                {opt.name}
              </Typography>
            </MenuItem>
          ))}
        </Select>

        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </Box>
  );
}
